import { useTranslation } from 'react-i18next';

import { AccountLayout } from '@components/layout/AccountLayout';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = [9, 10, 11, 12, 13, 14, 15, 16, 17];

const GHOST_EVENTS = [
  { day: 1, startSlot: 0, span: 2 },
  { day: 1, startSlot: 4, span: 1 },
  { day: 2, startSlot: 1, span: 3 },
  { day: 3, startSlot: 0, span: 1 },
  { day: 3, startSlot: 3, span: 2 },
  { day: 4, startSlot: 2, span: 1 },
  { day: 5, startSlot: 1, span: 2 },
  { day: 5, startSlot: 5, span: 1 },
  { day: 6, startSlot: 3, span: 1 },
];

function WeekGridGhost() {
  const slotH = 28;

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-white/60">
      {/* Day headers */}
      <div className="grid border-b border-border" style={{ gridTemplateColumns: '48px repeat(7, 1fr)' }}>
        <div className="border-r border-border" />
        {DAYS.map((d) => (
          <div key={d} className="border-r border-border px-2 py-2.5 last:border-r-0">
            <div className="mx-auto h-2 w-6 rounded-full bg-muted/50" />
          </div>
        ))}
      </div>

      {/* Time slots */}
      <div className="relative grid" style={{ gridTemplateColumns: '48px repeat(7, 1fr)' }}>
        {/* Hour labels */}
        <div className="border-r border-border">
          {HOURS.map((h) => (
            <div
              key={h}
              className="flex items-start justify-end border-b border-border/50 pr-2 pt-1 last:border-b-0"
              style={{ height: slotH }}
            >
              <div className="h-2 w-5 rounded-full bg-muted/40" />
            </div>
          ))}
        </div>

        {/* Day columns */}
        {DAYS.map((d, dayIdx) => {
          const events = GHOST_EVENTS.filter((e) => e.day === dayIdx);
          return (
            <div key={d} className="relative border-r border-border last:border-r-0">
              {HOURS.map((h) => (
                <div
                  key={h}
                  className="border-b border-border/40 last:border-b-0"
                  style={{ height: slotH }}
                />
              ))}
              {events.map((ev, i) => (
                <div
                  key={i}
                  className="absolute inset-x-1 rounded-md bg-primary/25"
                  style={{
                    top: ev.startSlot * slotH + 2,
                    height: ev.span * slotH - 4,
                  }}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MiniMonthGhost() {
  return (
    <div className="rounded-2xl border border-border bg-white/60 p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="h-2.5 w-20 rounded-full bg-muted/60" />
        <div className="flex gap-2">
          <div className="h-5 w-5 rounded bg-muted/40" />
          <div className="h-5 w-5 rounded bg-muted/40" />
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: 35 }).map((_, i) => (
          <div
            key={i}
            className={[
              'mx-auto h-6 w-6 rounded-full',
              i === 14 ? 'bg-primary/30' : 'bg-muted/30',
            ].join(' ')}
          />
        ))}
      </div>
    </div>
  );
}

export function CalendarPage() {
  const { t } = useTranslation();

  return (
    <AccountLayout>
      <div
        className="relative min-h-[calc(100vh-4rem)] overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse 70% 55% at 70% -5%, hsl(160 51% 24% / 0.08) 0%, transparent 70%), radial-gradient(ellipse 50% 40% at -5% 90%, hsl(160 51% 24% / 0.06) 0%, transparent 65%), hsl(var(--color-background))',
        }}
      >
        {/* Ring ornament — top right */}
        <div className="pointer-events-none absolute -right-24 -top-24 text-primary" aria-hidden="true">
          <svg width="480" height="480" viewBox="0 0 480 480" fill="none">
            <circle cx="240" cy="240" r="200" stroke="currentColor" strokeWidth="1"   opacity="0.08" />
            <circle cx="240" cy="240" r="150" stroke="currentColor" strokeWidth="1"   opacity="0.07" />
            <circle cx="240" cy="240" r="100" stroke="currentColor" strokeWidth="1.5" opacity="0.06" />
            <circle cx="240" cy="240" r="50"  stroke="currentColor" strokeWidth="2"   opacity="0.05" />
          </svg>
        </div>

        {/* Ring ornament — bottom left */}
        <div className="pointer-events-none absolute -bottom-40 -left-40 text-primary" aria-hidden="true">
          <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
            <circle cx="200" cy="200" r="160" stroke="currentColor" strokeWidth="1"   opacity="0.06" />
            <circle cx="200" cy="200" r="110" stroke="currentColor" strokeWidth="1"   opacity="0.05" />
            <circle cx="200" cy="200" r="60"  stroke="currentColor" strokeWidth="1.5" opacity="0.04" />
          </svg>
        </div>

        <div className="relative mx-auto flex max-w-4xl flex-col items-center px-4 pb-20 pt-16 sm:pt-20">

          {/* Icon mark */}
          <div className="mb-6 flex h-[72px] w-[72px] items-center justify-center rounded-2xl bg-primary shadow-md">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>

          {/* Badge */}
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">
              {t('layout.account.comingSoon')}
            </span>
          </div>

          {/* Title */}
          <h1 className="mb-3 font-display text-5xl font-semibold text-foreground sm:text-6xl">
            {t('layout.account.nav.calendar')}
          </h1>

          {/* Description */}
          <p className="mb-14 max-w-xs text-center text-base leading-relaxed text-muted-foreground">
            Schedule appointments and manage your availability — all in one place.
          </p>

          {/* Ghost widgets */}
          <div className="w-full space-y-3 opacity-40">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_200px]">
              <WeekGridGhost />
              <MiniMonthGhost />
            </div>
          </div>

        </div>
      </div>
    </AccountLayout>
  );
}
